import { getState } from '~src/state';
import { filterSpellCheck } from '~core/spellcheck';
import { SpellCheckTextbox } from './SpellCheckTextbox';
import { FEATURES, isFeatureEnabled } from '~src/features';

/**
 * Check spelling for textboxes (single or multiple)
 * @param {fabric.Canvas|Array|Object} target - Canvas (to check all textboxes), array of textboxes, or single textbox/event
 * @returns {Promise<Array>} Array of results with textbox and spell check data
 */
export async function checkSpelling(target) {
  // Check if spellcheck feature is enabled
  const allowSpellcheck = await isFeatureEnabled(FEATURES.SPELLCHECK);
  if (!allowSpellcheck) {
    return [];
  }

  // Check if spellcheck is enabled in settings
  const settings = getState('settings');
  if (settings && !settings.spellCheckEnabled) {
    return [];
  }

  const spellChecker = getState('spellChecker');
  if (!spellChecker) {
    throw new Error('SpellChecker not initialized');
  }

  let textboxes = [];

  // Determine what textboxes to check based on target type
  if (target && typeof target.getObjects === 'function') {
    // It's a canvas - get all SpellCheckTextbox instances
    textboxes = target.getObjects().filter((obj) => {
      return (
        obj instanceof SpellCheckTextbox &&
        obj.text &&
        obj.text.trim().length > 0
      );
    });
  } else if (Array.isArray(target)) {
    // It's an array of textboxes
    textboxes = target.filter((obj) => {
      return (
        obj instanceof SpellCheckTextbox &&
        obj.text &&
        obj.text.trim().length > 0
      );
    });
  } else if (target) {
    // It's a single textbox or event object
    const textbox = target.target || target;
    if (
      textbox instanceof SpellCheckTextbox &&
      textbox.text &&
      textbox.text.trim().length > 0
    ) {
      textboxes = [textbox];
    }
  }

  if (textboxes.length === 0) {
    return [];
  }

  // Create promises for parallel spell checking
  const promises = textboxes.map(async (textbox) => {
    try {
      const result = await spellChecker.check(textbox.text);
      const filteredResult = filterSpellCheck(result);

      // Apply results to textbox
      if (
        textbox.setSpellCheckResults &&
        typeof textbox.setSpellCheckResults === 'function'
      ) {
        textbox.setSpellCheckResults(filteredResult);
      }

      return {
        textbox,
        success: true,
        result: filteredResult,
        errorCount: filteredResult.matches?.length || 0,
      };
    } catch (error) {
      // Clear any existing spell check results on error
      if (
        textbox.clearSpellCheckResults &&
        typeof textbox.clearSpellCheckResults === 'function'
      ) {
        textbox.clearSpellCheckResults();
      }

      return {
        textbox,
        success: false,
        error: error.message,
        errorCount: 0,
      };
    }
  });

  const results = await Promise.all(promises);

  // Request render if we have a canvas to work with
  if (target && typeof target.requestRenderAll === 'function') {
    target.requestRenderAll();
  } else if (textboxes.length > 0 && window.canvas) {
    window.canvas.requestRenderAll();
  }

  return results;
}

export default checkSpelling;
